import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

// ── Number to Words ──────────────────────────────────────────────────────────
const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
const tensW = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
function numberToWords(num) {
  if (!num || isNaN(num)) return "Zero";
  const n = Math.floor(parseFloat(num));
  if (n === 0) return "Zero";
  if (n < 20) return ones[n];
  if (n < 100) return tensW[Math.floor(n/10)] + (n%10 ? " "+ones[n%10] : "");
  if (n < 1000) return ones[Math.floor(n/100)]+" Hundred"+(n%100 ? " "+numberToWords(n%100) : "");
  if (n < 100000) return numberToWords(Math.floor(n/1000))+" Thousand"+(n%1000 ? " "+numberToWords(n%1000) : "");
  if (n < 10000000) return numberToWords(Math.floor(n/100000))+" Lakh"+(n%100000 ? " "+numberToWords(n%100000) : "");
  return numberToWords(Math.floor(n/10000000))+" Crore"+(n%10000000 ? " "+numberToWords(n%10000000) : "");
}

const fmt = (v) =>
  v !== undefined && v !== null && !isNaN(parseFloat(v))
    ? parseFloat(v).toFixed(2)
    : "0.00";

const FY_MONTHS    = ["APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC","JAN","FEB","MAR"];
const monthToFyIdx = {"04":0,"05":1,"06":2,"07":3,"08":4,"09":5,"10":6,"11":7,"12":8,"01":9,"02":10,"03":11};
const MONTH_NAMES  = ["","January","February","March","April","May","June","July","August","September","October","November","December"];

const B     = { borderWidth:0.75, borderColor:"#000", borderStyle:"solid" };
const YTD_W = 56;
const AMT_W = 56;

const S = StyleSheet.create({
  page:      { fontFamily:"Helvetica", fontSize:8, padding:"18px 24px", color:"#000", backgroundColor:"#fff" },
  title:     { fontSize:11, fontFamily:"Helvetica-Bold", textAlign:"center", textDecoration:"underline", letterSpacing:1, marginBottom:2 },
  subTitle:  { fontSize:7.5, textAlign:"center", marginBottom:5 },
  row:       { flexDirection:"row" },
  empSecHdr: { ...B, padding:"3.5px 5px", fontSize:8, fontFamily:"Helvetica-Bold", backgroundColor:"#d0d0d0", flex:1 },
  empLabel:  { ...B, padding:"3px 5px", fontSize:7.5, fontFamily:"Helvetica-Bold", backgroundColor:"#f0f0f0", flex:0.85 },
  empVal:    { ...B, padding:"3px 5px", fontSize:7.5, flex:1.15 },
  colHdr:    { ...B, padding:"3px 5px", fontSize:7.5, fontFamily:"Helvetica-Bold", backgroundColor:"#e8e8e8" },
  nc:        { ...B, padding:"2.5px 4px", fontSize:7, flex:1, minHeight:15 },
  ncBold:    { ...B, padding:"2.5px 4px", fontSize:7, fontFamily:"Helvetica-Bold", flex:1, minHeight:15 },
  ncGrey:    { ...B, padding:"2.5px 4px", fontSize:7, fontFamily:"Helvetica-Bold", flex:1, minHeight:15, backgroundColor:"#e0e0e0" },
  ac:        { ...B, padding:"2.5px 4px", fontSize:7, width:YTD_W, textAlign:"right", minHeight:15 },
  acBold:    { ...B, padding:"2.5px 4px", fontSize:7, fontFamily:"Helvetica-Bold", width:YTD_W, textAlign:"right", minHeight:15 },
  acGrey:    { ...B, padding:"2.5px 4px", fontSize:7, fontFamily:"Helvetica-Bold", width:YTD_W, textAlign:"right", minHeight:15, backgroundColor:"#e0e0e0" },
  mCell:     { ...B, width:"16.66%", padding:"3px 0", textAlign:"center", fontSize:7 },
  mCellOn:   { ...B, width:"16.66%", padding:"3px 0", textAlign:"center", fontSize:7, backgroundColor:"#555" },
  secHdr:    { ...B, padding:"3.5px 5px", fontSize:8, fontFamily:"Helvetica-Bold", backgroundColor:"#d0d0d0", flex:1 },
  inWords:   { fontSize:7.5, marginTop:4, marginBottom:4 },
});

// ── Components ────────────────────────────────────────────────────────────────
const EmpRow = ({ l1,v1,l2,v2 }) => (
  <View style={S.row}>
    <View style={S.empLabel}><Text>{l1}</Text></View>
    <View style={S.empVal}><Text>{v1||""}</Text></View>
    <View style={S.empLabel}><Text>{l2}</Text></View>
    <View style={S.empVal}><Text>{v2||""}</Text></View>
  </View>
);

// Single unified row — earn + ded side by side, always aligned
const URow = ({
  eName="", eYtd="", eAmt="", eBold=false, eGrey=false,
  dName="", dYtd="", dAmt="", dBold=false, dGrey=false,
}) => {
  const eN = eGrey ? S.ncGrey : eBold ? S.ncBold : S.nc;
  const eA = eGrey ? S.acGrey : eBold ? S.acBold : S.ac;
  const dN = dGrey ? S.ncGrey : dBold ? S.ncBold : S.nc;
  const dA = dGrey ? S.acGrey : dBold ? S.acBold : S.ac;
  return (
    <View style={S.row}>
      <View style={[eN,{flex:1}]}><Text>{eName}</Text></View>
      <View style={eA}><Text>{eYtd}</Text></View>
      <View style={eA}><Text>{eAmt}</Text></View>
      <View style={[dN,{flex:1}]}><Text>{dName}</Text></View>
      <View style={dA}><Text>{dYtd}</Text></View>
      <View style={dA}><Text>{dAmt}</Text></View>
    </View>
  );
};

// ── Lookup helpers ────────────────────────────────────────────────────────────
// Find from a combined list by keyword (case-insensitive)
const findByKey = (list, keyword) =>
  list.find(a => (a.name||"").toLowerCase().includes(keyword.toLowerCase()));

// ── Main Component ─────────────────────────────────────────────────────────────
const SalarySlip = ({ mainData, userDetails, month, companyDetails, earnedSalary, logo, companyName }) => {
  const emp    = userDetails?.[0] || {};
  const sal    = mainData         || {};
  const earned = earnedSalary     || {};

  // Employee fields
  const empId       = emp.employee_id   || emp.emp_id        || emp.staffId          || "";
  const empName     = emp.full_name     || `${emp.firstName||""} ${emp.lastName||""}`.trim() || "";
  const pfNo        = emp.pfNumber      || emp.pf_number      || "";
  const esiNo       = emp.esiNumber     || emp.esi_number     || "";
  const netPayDays  = earned.netPayDays || earned.net_pay_days|| "";
  const presentDays = earned.presentDays|| earned.present_days|| "";
  const doj         = emp.dateOfJoining || emp.doj            || "";
  const designation = emp.designation   || emp.emp_designation|| "";
  const department  = emp.department    || emp.emp_department || "";
  const branch      = emp.branch        || emp.branchName     || "";
  const bankAc      = emp.bankDetails?.bankAccountNumber      || "";
  const ifsc        = emp.bankDetails?.ifscCode               || "";
  const pan         = emp.panNumber     || emp.pan_number     || "";
  const uan         = emp.uanNumber     || emp.uan_number     || "";
  const lop         = earned.lop        || earned.lopDays     || "0";

  // ── All lists from API ────────────────────────────────────────────────────
  const allowanceList    = sal.allowanceList    || [];
  const newAllowanceList = sal.newAllowanceList || [];

  // Combined list to search from (allowanceList + newAllowanceList)
  const allList = [...allowanceList, ...newAllowanceList];

  // ── Helper: get a row by display label + search keywords ─────────────────
  // Returns { name, ytd, amt } or null if not found / zero
  const getRow = (displayName, ...keywords) => {
    for (const kw of keywords) {
      const found = findByKey(allList, kw);
      if (found) {
        return {
          name: displayName,
          ytd:  fmt(found.yearlyAmount),
          amt:  fmt(found.monthlyAmount),
        };
      }
    }
    return null;
  };

  // ── FIXED EARNING FIELD ORDER (matches your UI exactly) ───────────────────
  // Each entry: always show, use 0.00 if not found (so blank rows don't appear/disappear)
  const EARNING_FIELDS = [
    { label: "Basic",             direct: { m: sal.basicPay,        y: sal.basicPayYearly        } },
    { label: "DRA",               keys:   ["dra","dearness"]                                       },
    { label: "HRA",               direct: { m: sal.hraPay,          y: sal.hraPayYearly          } },
    { label: "Special Allowance", keys:   ["special allowance","special"]                          },
    { label: "Incentives",        keys:   ["incentive"]                                            },
    { label: "Allowance",         keys:   ["^allowance","allowance"]                               },
    { label: "Statutory Bonus",   keys:   ["statutory"]                                            },
    { label: "Variable Pay",      keys:   ["variable"]                                             },
    { label: "Attendance Bonus",  keys:   ["attendance"]                                           },
    { label: "Conveyance",        keys:   ["conveyance"]                                           },
    { label: "Medical Allowance", keys:   ["medical"]                                              },
  ];

  const earningRows = EARNING_FIELDS.map(field => {
    if (field.direct) {
      return {
        name: field.label,
        ytd:  fmt(field.direct.y),
        amt:  fmt(field.direct.m),
      };
    }
    // Search allList for this field
    for (const kw of field.keys) {
      const found = findByKey(allList, kw);
      if (found) {
        return {
          name: field.label,
          ytd:  fmt(found.yearlyAmount),
          amt:  fmt(found.monthlyAmount),
        };
      }
    }
    // Not found — show field with 0.00 so the row is always present
    return { name: field.label, ytd: "0.00", amt: "0.00" };
  });

  // ── FIXED DEDUCTION FIELD ORDER (matches your UI exactly) ─────────────────
  // Deductions: Provident Fund, ESI, Professional Tax, Deduction (If any), Insurance, TDS
  const getDeductionRow = (displayName, directM, directY, ...keywords) => {
    if (directM !== undefined && directM !== null) {
      return { name: displayName, ytd: fmt(directY), amt: fmt(directM) };
    }
    for (const kw of keywords) {
      const found = findByKey(allList, kw);
      if (found) {
        return { name: displayName, ytd: fmt(found.yearlyAmount), amt: fmt(found.monthlyAmount) };
      }
    }
    return { name: displayName, ytd: "0.00", amt: "0.00" };
  };

  const deductionRows = [
    // 1. Provident Fund
    getDeductionRow("Provident Fund",    sal.employeePf,      sal.employeePfYearly,      "provident","pf"),
    // 2. ESI
    getDeductionRow("ESI",               sal.employeeEsi,     sal.employeeEsiYearly,     "esi"),
    // 3. Professional Tax
    getDeductionRow("Professional Tax",  sal.professionalTax, sal.professionalTaxYearly, "professional tax","prof tax"),
    // 4. Deduction (If any) — LWF or custom deduction
    getDeductionRow("Deduction (If any)", sal.lwfAmount,      undefined,                 "lwf","deduction"),
    // 5. Insurance
    getDeductionRow("Insurance",          undefined,           undefined,                 "insurance"),
    // 6. TDS
    getDeductionRow("TDS",                sal.tdsAmount,       sal.tdsAmountYearly,       "tds"),
  ];

  // ── Salary totals ─────────────────────────────────────────────────────────
  const grossM = fmt(sal.grossSalary);
  const netM   = fmt(sal.netSalary);

  // Total deductions = sum of all deduction amounts
  const totalDeductM = fmt(
    deductionRows.reduce((sum, d) => sum + parseFloat(d.amt || 0), 0)
  );

  // ── Pad both arrays to same length ────────────────────────────────────────
  // Earning has 11 fields, deduction has 6 — pad deduction to 11 with blanks
  const BLANK   = { name:"", ytd:"", amt:"" };
  const dataLen = Math.max(earningRows.length, deductionRows.length);
  const ePadded = [...earningRows,   ...Array(Math.max(0, dataLen - earningRows.length)).fill(BLANK)];
  const dPadded = [...deductionRows, ...Array(Math.max(0, dataLen - deductionRows.length)).fill(BLANK)];

  // ── Summary rows ──────────────────────────────────────────────────────────
  const eSummary = [
    { name:"Total Earnings",   ytd:"", amt:grossM,       bold:false },
    { name:"Total Deductions", ytd:"", amt:totalDeductM, bold:false },
    { name:"Net Pay",          ytd:"", amt:netM,         bold:true  },
  ];
  const dSummary = [
    { name:"Reductions",       ytd:"", amt:"",           bold:false },
    { name:"Total Deductions", ytd:"", amt:totalDeductM, bold:false },
    { name:"",                 ytd:"", amt:"",           bold:false },
  ];

  // In words
  const netInWords = numberToWords(Math.floor(parseFloat(sal.netSalary||0))) + " Rupees Only";

  // Month highlight
  const [yr, mn]    = (month||"").split("-");
  const activeFyIdx = mn ? (monthToFyIdx[mn] ?? -1) : -1;
  const subLine     = [companyName, mn ? `${MONTH_NAMES[parseInt(mn)||0]||""} ${yr}` : ""].filter(Boolean).join(" | ");

  return (
    <Document>
      <Page size="A4" style={S.page}>

        {/* TITLE */}
        <Text style={S.title}>SALARY SLIP</Text>
        {subLine ? <Text style={S.subTitle}>{subLine}</Text> : null}

        {/* EMPLOYEE DETAILS */}
        <View>
          <View style={S.row}><View style={S.empSecHdr}><Text>Employee Details</Text></View></View>
          <EmpRow l1="Emp ID"       v1={empId}       l2="Employee Name" v2={empName}       />
          <EmpRow l1="PF No"        v1={pfNo}         l2="ESI No"        v2={esiNo}         />
          <EmpRow l1="Net Pay Days" v1={netPayDays}   l2="Present Days"  v2={presentDays}   />
          <EmpRow l1="DOJ"          v1={doj}          l2="Designation"   v2={designation}   />
          <EmpRow l1="Department"   v1={department}   l2="Branch"        v2={branch}        />
          <EmpRow l1="Bank A/c"     v1={bankAc}       l2="IFSC"          v2={ifsc}          />
          <EmpRow l1="PAN"          v1={pan}          l2="Mode of Pay"   v2="Bank Transfer" />
          <EmpRow l1="UAN"          v1={uan}          l2="LOP"           v2={lop}           />
        </View>

        {/* EARNINGS + DEDUCTIONS — unified single table */}
        <View style={{ marginTop:5 }}>

          {/* Column headers */}
          <View style={S.row}>
            <View style={[S.colHdr,{flex:1}]}><Text>Earnings</Text></View>
            <View style={[S.colHdr,{width:YTD_W}]}><Text style={{textAlign:"right"}}>YTD</Text></View>
            <View style={[S.colHdr,{width:AMT_W}]}><Text style={{textAlign:"right"}}>Amount</Text></View>
            <View style={[S.colHdr,{flex:1}]}><Text>Deductions</Text></View>
            <View style={[S.colHdr,{width:YTD_W}]}><Text style={{textAlign:"right"}}>YTD</Text></View>
            <View style={[S.colHdr,{width:AMT_W}]}><Text style={{textAlign:"right"}}>Amount</Text></View>
          </View>

          {/* Data rows — earn[i] and ded[i] always in the SAME physical row */}
          {ePadded.map((e, i) => (
            <URow
              key={i}
              eName={e.name}           eYtd={e.ytd}           eAmt={e.amt}
              dName={dPadded[i].name}  dYtd={dPadded[i].ytd}  dAmt={dPadded[i].amt}
            />
          ))}

          {/* Grey section-header — "Summary" | "Deductions" in same row */}
          <URow eGrey eName="Summary" dGrey dName="Deductions" />

          {/* Summary rows */}
          {eSummary.map((e, i) => (
            <URow
              key={"s"+i}
              eName={e.name}            eYtd={e.ytd}  eAmt={e.amt}  eBold={e.bold}
              dName={dSummary[i].name}  dYtd={dSummary[i].ytd} dAmt={dSummary[i].amt} dBold={dSummary[i].bold}
            />
          ))}
        </View>

        {/* IN WORDS */}
        <Text style={S.inWords}>In Words : {netInWords}</Text>

        {/* BOTTOM — Tax Regime | TDS Details */}
        <View style={S.row}>

          {/* LEFT */}
          <View style={{flex:1, marginRight:3}}>
            <View style={S.row}><View style={S.secHdr}><Text>New Tax Regime Opted</Text></View></View>
            {[0,1,2,3].map(i=>(
              <View style={S.row} key={i}>
                <View style={[S.nc,{flex:1}]}><Text> </Text></View>
                <View style={[S.nc,{flex:1}]}><Text> </Text></View>
              </View>
            ))}
            <View style={{marginTop:7}}>
              {[
                {label:"Total Earnings",   val:grossM      },
                {label:"Total Deductions", val:totalDeductM},
                {label:"Net Pay",          val:netM        },
              ].map((item,i)=>(
                <View key={i} style={{flexDirection:"row", alignItems:"center", marginBottom:4}}>
                  <Text style={{fontSize:7.5, width:85}}>{item.label}</Text>
                  <View style={{flex:1, borderBottom:"0.75px solid #000"}} />
                  <Text style={{fontSize:7.5, width:60, textAlign:"right"}}>{item.val}</Text>
                </View>
              ))}
            </View>
          </View>

          {/* RIGHT */}
          <View style={{flex:1.6}}>
            <View style={S.row}><View style={S.secHdr}><Text>TDS Details</Text></View></View>
            <View style={S.row}>
              <View style={[S.colHdr,{flex:1.6}]}><Text>Description</Text></View>
              <View style={[S.colHdr,{flex:1}]}><Text style={{textAlign:"right"}}>Gross</Text></View>
              <View style={[S.colHdr,{flex:1}]}><Text style={{textAlign:"right"}}>Exempt</Text></View>
              <View style={[S.colHdr,{flex:1}]}><Text style={{textAlign:"right"}}>Taxable</Text></View>
            </View>
            {[0,1,2,3].map(i=>(
              <View style={S.row} key={i}>
                <View style={[S.nc,{flex:1.6}]}><Text> </Text></View>
                <View style={[S.ac,{flex:1,width:"auto"}]}><Text></Text></View>
                <View style={[S.ac,{flex:1,width:"auto"}]}><Text></Text></View>
                <View style={[S.ac,{flex:1,width:"auto"}]}><Text></Text></View>
              </View>
            ))}
            <View style={{marginTop:5}}>
              <View style={{flexDirection:"row", flexWrap:"wrap"}}>
                {FY_MONTHS.map((m,i)=>(
                  <View key={i} style={i===activeFyIdx ? S.mCellOn : S.mCell}>
                    <Text style={i===activeFyIdx ? {color:"#fff"} : {}}>{m}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>

        <Text style={{fontSize:6.5, marginTop:8, color:"#555", textAlign:"center"}}>
          This is a computer generated salary slip and does not require a signature.
        </Text>

      </Page>
    </Document>
  );
};

export default SalarySlip;